import React from 'react';

import { formatDate, formatMoney, toMoney, track, useGraphQLQuery } from '@betterment/js-runtime';
import { Box, Icon, Text } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';
import { SambaAutoLink } from '@betterment/shared-components';
import { Card, Flex, HR, StackedProgressBar } from '@betterment/style-closet-react';

import CardShimmerLoader from './cardShimmerLoader';
import { progressBarData } from './utils';

import { urlBuilder } from '../../Shared/urlBuilder';
import { IraContributionMatchGrantsDocument } from '../graphql/query.iraContributionMatchGrants.generated';

const EARNED_MATCH_AMOUNT_COLOR = 'dataviz.teal.1';
const PROJECTED_MATCH_AMOUNT_COLOR = 'dataviz.teal.3';

type ProjectedMatchAmountTextProps = {
  projectedMatchAmountCents: number;
};

export const ProjectedMatchAmountText: React.FC<ProjectedMatchAmountTextProps> = ({ projectedMatchAmountCents }) => {
  if (projectedMatchAmountCents > 0) {
    return (
      <Text variant="text50" color="text.weak" as="p">
        Based on upcoming recurring deposits
      </Text>
    );
  }

  return (
    <Text variant="text50" color="text.weak" as="p">
      <SambaAutoLink
        to={urlBuilder.appDepositSetup.absolute()}
        onClick={() => {
          track('ElementClicked', {
            name: 'RecurringTransactionPrompt',
            module: 'IraMatchDetails',
            flow: 'IraDetailsApp',
          });
        }}
      >
        Set up recurring transaction
      </SambaAutoLink>
    </Text>
  );
};

const IraMatchOfferCard: React.FC<React.PropsWithChildren> = () => {
  const { data, loading: isLoading } = useGraphQLQuery(IraContributionMatchGrantsDocument);

  if (!data) {
    return null;
  }

  const grant = data.iraContributionMatchGrants[0];

  if (!grant) {
    return null;
  }

  const { contributionWindow, disclosureUrl, projectedMatchAmountV3, matchEligibleAmount, earnedMatchAmount } = grant;

  const projectedMatchAmountCents = projectedMatchAmountV3.cents;
  const matchEligibleAmountCents = matchEligibleAmount?.cents;
  const earnedMatchAmountCents = earnedMatchAmount.cents;

  return (
    <Box css={{ pt: 'space.200' }}>
      {isLoading && <CardShimmerLoader />}
      {!isLoading && (
        <Card isRounded={true}>
          <Card.Slot name="header">Your Betterment match</Card.Slot>
          <Card.Slot name="body">
            <Box>
              <Text>{`Earn ${
                grant.match
              }% on your net Roth and Traditional IRA contributions made at Betterment now through ${formatDate(
                contributionWindow.end,
                'longOrdinal'
              )}.`}</Text>
            </Box>
            <Box css={{ pt: 'space.200' }}>
              <Text variant="text50" css={{ color: 'text.weak' }}>
                Your match is at the discretion of Betterment and is subject to change.{' '}
                <SambaAutoLink to={disclosureUrl} target="_blank">
                  See terms
                </SambaAutoLink>
              </Text>
            </Box>
            {matchEligibleAmountCents && (
              <Box css={{ pt: 'space.300' }}>
                <StackedProgressBar
                  percents={progressBarData({
                    matchEligibleAmountCents,
                    projectedMatchAmountCents,
                    earnedMatchAmountCents,
                    projectedMatchAmountColor: token.var(`colors.${PROJECTED_MATCH_AMOUNT_COLOR}`),
                    earnedMatchAmountColor: token.var(`colors.${EARNED_MATCH_AMOUNT_COLOR}`),
                  })}
                />
                <Flex justifyContent="space-between">
                  <Flex mt="1">
                    <Text variant="text50" color="text.weak">
                      {formatMoney(toMoney(0), 'defaultDollar')}
                    </Text>
                  </Flex>
                  <Flex mt="1">
                    <Flex>
                      <Text variant="text50" color="text.weak">
                        Eligible: {formatMoney(toMoney(matchEligibleAmountCents), 'default')}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Box>
            )}
            <Box>
              <Flex justifyContent="space-between" my="6" alignItems="center">
                <Flex alignItems="center">
                  <Flex>
                    <Icon name="Bullet" size="small" color={EARNED_MATCH_AMOUNT_COLOR} />
                  </Flex>
                  <Box css={{ pr: 'space.150' }}>
                    <Text variant="text100">Match earned</Text>
                  </Box>
                </Flex>
                <Text>{formatMoney(toMoney(earnedMatchAmountCents))}</Text>
              </Flex>
              <HR />
            </Box>
            <Box>
              <Flex justifyContent="space-between" my="6" alignItems="center">
                <Flex alignItems="center">
                  <Box>
                    <Icon name="Bullet" size="small" color={PROJECTED_MATCH_AMOUNT_COLOR} />
                  </Box>
                  <Box css={{ pr: 'space.150' }}>
                    <Text variant="text100">Projected match</Text>
                    <ProjectedMatchAmountText projectedMatchAmountCents={projectedMatchAmountCents} />
                  </Box>
                </Flex>
                <Text>{formatMoney(toMoney(projectedMatchAmountCents))}</Text>
              </Flex>
            </Box>
          </Card.Slot>
        </Card>
      )}
    </Box>
  );
};

export default IraMatchOfferCard;
